import { logoutApi, useMe } from "@/api/services/main/auth";
import { IconBackArrow, IconChevronDown, IconForwardArrow, IconLogo, IconNotification } from "@/assets/icons";
import { ConfirmModal } from "@/components/commons/ConfirmModal";
import { Popover } from "@/components/elements/Popover";
import { RuntimeEnvTag } from "@/components/elements/RuntimeEnvTag";
import { HelpHeaderTooltip } from "@/components/layout/elements/AppHeader/HelpHeaderTooltip";
import { NotificationModal } from "@/features/information/components/NotificationModal";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTokenClaims } from "@/hooks/useTokenClaims";
import { useUnreadNotification } from "@/hooks/useUnreadNotification";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { background, divider, text } from "@/theme/colors";
import { elevation, spacing } from "@/theme/variables";
import { ADMIN_CENTER_URL, CENTER_URL, getCenterProductContractId } from "@/utils/oauth";
import { redirectToLogin } from "@/utils/redirectToLogin";
import { truncateWithEllipsis } from "@/utils/string";
import { createUrlWithParams, getDynamicRoute } from "@/utils/url";
import { Box, Button, css, Divider, Stack, styled, Tooltip, Typography } from "@mui/material";
import zIndex from "@mui/material/styles/zIndex";
import Cookies from "js-cookie";
import { FC } from "react";
import { Link } from "react-router-dom";

type TAppHeaderProps = {
  isOpenDrawer: boolean;
  toggleDrawer: () => void;
};

export const AppHeader: FC<TAppHeaderProps> = ({ isOpenDrawer, toggleDrawer }) => {
  const { isLaptop } = useMediaQuery();
  const { data: meResult } = useMe();
  const meData = meResult?.data;
  const checkResult = useUnreadNotification();
  const notificationModal = useVisible();
  const logoutConfirmModal = useVisible();
  const { organizationId } = useTokenClaims();

  const handleLogout = async () => {
    try {
      const refreshToken = Cookies.get("refresh_token") ?? "";
      await logoutApi({ refreshToken });
    } finally {
      redirectToLogin({ clearToken: true });
    }
  };

  const goToOrganizationSetting = () => {
    if (!CENTER_URL) return;
    window.location.href = CENTER_URL;
  };

  const goToUserSettingDetail = () => {
    if (!CENTER_URL) return;
    window.location.href = `${CENTER_URL}/user-setting/detail`;
  };

  const goToCenterAdmin = () => {
    if (!ADMIN_CENTER_URL) return;
    window.location.href = `${ADMIN_CENTER_URL}/organizations/${organizationId}`;
  };

  const goToMatchAdmin = () => {
    if (!ADMIN_CENTER_URL) return;
    window.location.href = createUrlWithParams(`${ADMIN_CENTER_URL}/mint-oauth-redirect`, {
      productContractId: getCenterProductContractId(),
    });
  };

  const goToOrganizationSelect = () => {
    if (!ADMIN_CENTER_URL) return;
    window.location.href = `${ADMIN_CENTER_URL}/session/organization-select`;
  };

  return (
    <BoxWrapper>
      <Stack direction="row" alignItems="center" gap={1}>
        {isLaptop && (
          <Tooltip title={isOpenDrawer ? "メニューを最小化" : "メニューを最大化"} placement="bottom-start">
            <Button onClick={toggleDrawer}>
              {isOpenDrawer ? <IconBackArrow width={32} height={32} /> : <IconForwardArrow width={32} height={32} />}
            </Button>
          </Tooltip>
        )}
        <StyledLink to={AppRoutes.home}>
          <IconLogo />
        </StyledLink>
        <RuntimeEnvTag />
      </Stack>
      <AppMenu mt={1}>
        <Box mr={2} mb={0.5}>
          <HelpHeaderTooltip />
        </Box>
        <Typography onClick={notificationModal.open}>
          <IconNotification cursor="pointer" fontSize={24} hasDot={!!checkResult?.data} />
        </Typography>
        <NameTypography mb={1}>
          {meData
            ? `${truncateWithEllipsis(meData?.match?.matchName ?? "", 20)}　|　${truncateWithEllipsis(meData?.fullName ?? "", 20)}`
            : "---- | ----"}
        </NameTypography>
        <Popover
          sx={{
            marginTop: "8px",
            borderRadius: 0,
            ".MuiPopover-paper": {
              borderRadius: "0 0 8px 8px",
              boxShadow: "0px 4px 4px 0px #00000040",
            },
          }}
          anchor={<IconChevronDown cursor="pointer" fontSize="24" color={text.white} />}
        >
          {meData?.isCenterAdminUser ? (
            <>
              {ADMIN_CENTER_URL && <CenterOption onClick={goToCenterAdmin}>centerシステム管理</CenterOption>}
              {ADMIN_CENTER_URL && <CenterOption onClick={goToMatchAdmin}>matchシステム管理</CenterOption>}
              <Divider />
              {ADMIN_CENTER_URL && <CenterOption onClick={goToOrganizationSelect}>顧客環境ログイン</CenterOption>}
            </>
          ) : (
            <>
              <CenterOption onClick={goToOrganizationSetting}>組織設定</CenterOption>
              <CenterOption onClick={goToUserSettingDetail}>ユーザー設定</CenterOption>
              <Divider />
              <CenterOption onClick={logoutConfirmModal.open}>ログアウト</CenterOption>
            </>
          )}
        </Popover>
      </AppMenu>
      <NotificationModal modal={notificationModal} />
      <ConfirmModal
        open={logoutConfirmModal.visible}
        onClose={logoutConfirmModal.close}
        onOk={handleLogout}
        title="ログアウト"
        message="すべてのirohanaサービスからログアウトされますがよろしいですか？"
      />
    </BoxWrapper>
  );
};

const BoxWrapper = styled(Box)`
  background-color: ${background.main};
  height: 56px;
  padding: 0px ${spacing.md};
  border-bottom: 1px solid ${divider.middle};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${elevation.lv1};
  position: sticky;
  top: 0;
  z-index: ${zIndex.appBar};
`;

const AppMenu = styled(Box)`
  display: flex;
  align-items: center;
`;

const NameTypography = styled(Typography)`
  color: ${text.white};
  margin-right: 13px;
  margin-left: 24px;
  font-size: 14px;
  line-height: 21px;
  font-weight: 400;
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
  &.centered {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const centerOptionCss = css`
  font-size: 14px;
  white-space: nowrap;
  text-align: center;
  padding: 4px 28px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  display: block;
  :hover {
    background: ${divider.low};
    color: ${text.primary};
  }
  :focus {
    background: ${divider.middle};
    color: ${text.primary};
  }
`;

const CenterOption = styled(Box)`
  ${centerOptionCss}
`;
